import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { MessageCircle, Bot, Send, Loader2, ChevronDown, LifeBuoy } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { API } from "../utils/api";
import { useAuth } from "../context/AuthContext";

// Generate or recover a stable session id per browser
function getSessionId() {
  let sid = sessionStorage.getItem("plazafy_chat_session");
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("plazafy_chat_session", sid);
  }
  return sid;
}

const SUGGESTIONS = [
  "¿Cómo reservo una plaza?",
  "¿Cómo publico mi plaza?",
  "¿Puedo cancelar mi reserva?",
  "¿Qué es Plazafy Gate?",
];

export default function Chatbot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({ name: "", email: "", message: "" });
  const [sendingTicket, setSendingTicket] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/history/${sessionId.current}`);
      if (res.data.messages?.length) {
        setMessages(res.data.messages);
      } else {
        setMessages([{
          role: "assistant",
          content: `¡Hola${user?.full_name ? ` ${user.full_name.split(" ")[0]}` : ""}! 👋 Soy Plazi, tu asistente de Plazafy. ¿En qué te ayudo?`
        }]);
      }
    } catch (_e) {
      setMessages([{
        role: "assistant",
        content: "¡Hola! 👋 Soy Plazi, tu asistente de Plazafy. ¿En qué te ayudo?"
      }]);
    }
  }, [user]);

  useEffect(() => {
    if (open && messages.length === 0) {
      loadHistory();
    }
  }, [open, messages.length, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const messageText = (text ?? input).trim();
    if (!messageText || sending) return;

    setMessages(prev => [...prev, { role: "user", content: messageText }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId.current,
        message: messageText
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Vaya, ha habido un problema técnico. Si urge, escríbenos a hola@plazafy.es o usa el botón de soporte aquí abajo."
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!supportForm.name.trim() || !supportForm.email.trim() || !supportForm.message.trim()) {
      toast.error("Completa todos los campos");
      return;
    }
    setSendingTicket(true);
    try {
      await axios.post(`${API}/chat/support-ticket`, {
        ...supportForm,
        session_id: sessionId.current
      });
      toast.success("Mensaje enviado. Te responderemos pronto a tu email.");
      setShowSupport(false);
      setSupportForm({ name: "", email: "", message: "" });
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Listo, he avisado al equipo. Te contestarán por email lo antes posible. ¿Puedo ayudarte con algo más?"
      }]);
    } catch (_e) {
      toast.error("No se pudo enviar el mensaje. Intenta más tarde.");
    } finally {
      setSendingTicket(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-violet-500 hover:bg-violet-600 text-white shadow-2xl shadow-violet-500/40 flex items-center justify-center transition-all hover:scale-110 group"
          aria-label="Abrir chat de soporte"
        >
          <Bot className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#1a1625]" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(600px,calc(100vh-3rem))] bg-[#231f30] rounded-2xl shadow-2xl shadow-black/50 border border-white/10 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-violet-600 to-violet-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold leading-tight">Plazi</p>
                <p className="text-white/70 text-xs flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> Online
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              data-testid="chatbot-close-btn"
              aria-label="Cerrar chat"
            >
              <ChevronDown className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Messages */}
          {!showSupport ? (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#1a1625]">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-violet-500 text-white rounded-br-sm"
                          : "bg-[#2a2438] text-gray-100 rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-[#2a2438] text-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestions on first load */}
                {messages.length === 1 && !sending && (
                  <div className="pt-2 flex flex-wrap gap-2">
                    {SUGGESTIONS.map(s => (
                      <button
                        key={s}
                        onClick={() => sendMessage(s)}
                        className="text-xs px-3 py-1.5 rounded-full bg-[#2a2438] hover:bg-[#332a45] text-gray-300 border border-white/5 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-white/5 p-3 bg-[#231f30]">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Escribe tu mensaje..."
                    disabled={sending}
                    data-testid="chatbot-input"
                    className="flex-1 bg-[#1a1625] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-violet-500"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={sending || !input.trim()}
                    data-testid="chatbot-send-btn"
                    className="bg-violet-500 hover:bg-violet-600 text-white px-3"
                    aria-label="Enviar"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                <button
                  onClick={() => {
                    setShowSupport(true);
                    setSupportForm(f => ({
                      ...f,
                      name: user?.full_name || user?.name || "",
                      email: user?.email || ""
                    }));
                  }}
                  className="mt-2 text-xs text-gray-400 hover:text-violet-400 flex items-center gap-1.5 transition-colors"
                  data-testid="chatbot-support-btn"
                >
                  <LifeBuoy className="w-3.5 h-3.5" />
                  ¿Necesitas hablar con un humano? Contactar soporte
                </button>
              </div>
            </>
          ) : (
            /* Support ticket form */
            <form onSubmit={submitTicket} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#1a1625]">
              <button
                type="button"
                onClick={() => setShowSupport(false)}
                className="text-xs text-gray-400 hover:text-violet-400 mb-2"
              >
                ← Volver al chat
              </button>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1.5">Tu nombre</label>
                <Input
                  value={supportForm.name}
                  onChange={(e) => setSupportForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="support-name-input"
                  className="bg-[#231f30] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1.5">Tu email</label>
                <Input
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => setSupportForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="support-email-input"
                  className="bg-[#231f30] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1.5">¿En qué podemos ayudarte?</label>
                <Textarea
                  value={supportForm.message}
                  onChange={(e) => setSupportForm(f => ({ ...f, message: e.target.value }))}
                  rows={5}
                  data-testid="support-message-input"
                  className="bg-[#231f30] border-white/10 text-white resize-none"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={sendingTicket}
                data-testid="support-submit-btn"
                className="w-full bg-violet-500 hover:bg-violet-600 text-white"
              >
                {sendingTicket ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                ) : "Enviar mensaje"}
              </Button>
              <p className="text-xs text-gray-500 text-center pt-1">
                Te responderemos a tu email lo antes posible.
              </p>
            </form>
          )}
        </div>
      )}
    </>
  );
}
